<script setup>
  import {ref, watch} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {MedButton, MedInfoButton} from "@pharma4u/medicheck-components";

  defineOptions({name: "RecordComponentBeforeAnalyseDialog"});

  defineProps({
    analysisButtonDisabled: {
      type: Boolean,
      required: true,
    },
  });

  defineEmits(["closeAndEmitAnalysisStart"]);

  const refRecordEditFinishedDialog = ref(null);

  const showUpgradeLabel = ref(false);
  const upgradeLabel = ref("");

  watch(showUpgradeLabel, (newValue) => {
    if (newValue) {
      upgradeLabel.value = "Upgrade Info";
    } else {
      upgradeLabel.value = "";
    }
  });

  const open = () => {
    refRecordEditFinishedDialog.value.open();
  };

  const close = () => {
    if (refRecordEditFinishedDialog.value) refRecordEditFinishedDialog.value.close();
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog ref="refRecordEditFinishedDialog" dialog-width="medium" :test-id="getTestId('analyse')">
    <template #title />

    <template #content>
      <div class="flex w-full justify-center space-x-2 text-mcgreen">
        <span class="text-xl">Eingabe abgeschlossen</span>

        <ComponentIcon class="mt-1 text-xl">check</ComponentIcon>
      </div>

      <hr class="my-4 h-px w-full p-0" />

      <div>
        <h4 class="mb-2 font-semibold">Alle Angaben korrekt?</h4>

        <h4 class="font-normal">Das wird analysiert:</h4>

        <ul class="mt-4 space-y-3">
          <li class="flex w-full items-start justify-between">
            <div class="flex items-center space-x-4">
              <ComponentIcon class="text-2xl text-mcgreen">check</ComponentIcon>

              <span>Medikationsanalyse</span>
            </div>

            <MedInfoButton
              size="lg"
              side="left"
              aria-label="Medikationsanalyse Info"
              disable-portal
              :data-test="getTestId('analyse-dialog-medication-info-button')"
            >
              <p class="max-w-sm text-sm">
                Strukturierte Prüfung auf arzneimittelbezogene Probleme (ABP), deren mögliche Ursachen sowie
                Lösungsvorschläge für aktuelle Beschwerden des Patienten.
              </p>
            </MedInfoButton>
          </li>

          <li class="flex w-full items-start justify-between">
            <div class="flex items-center space-x-4">
              <ComponentIcon class="text-2xl text-mcgreen">check</ComponentIcon>

              <span>Adhärenz</span>
            </div>

            <MedInfoButton
              size="lg"
              side="left"
              aria-label="Adhärenz Info"
              disable-portal
              :data-test="getTestId('analyse-dialog-adherence-info-button')"
            >
              <p class="max-w-sm text-sm">
                Es wird auf Therapietreue (Non-Adhärenz oder Non-Compliance) geprüft. Empfehlungen zu Maßnahmen, die die
                Therapietreue (Adhärenz) steigern können, werden angezeigt.
              </p>
            </MedInfoButton>
          </li>

          <li class="flex w-full items-start justify-between">
            <div class="flex items-center space-x-4">
              <ComponentIcon class="text-2xl text-mcgreen">check</ComponentIcon>

              <span>Prävention</span>
            </div>

            <MedInfoButton
              size="lg"
              side="left"
              aria-label="Prävention Info"
              disable-portal
              :data-test="getTestId('analyse-dialog-prevention-info-button')"
            >
              <p class="max-w-sm text-sm">
                Empfehlungen zu Präventionsmaßnahmen sind in kompakter Form verfügbar, wenn Sie Erkrankungen /
                Indikationen in der Eingabemaske angegeben haben.
              </p>
            </MedInfoButton>
          </li>
        </ul>
      </div>
    </template>

    <template #actions>
      <MedButton
        variant="primary"
        :disabled="analysisButtonDisabled"
        :data-test="getTestId('analyse-dialog-start-button')"
        @click="$emit('closeAndEmitAnalysisStart')"
      >
        Analysieren
      </MedButton>

      <MedButton :data-test="getTestId('analyse-dialog-cancel-button')" @click="close">Abbrechen</MedButton>
    </template>
  </ComponentDialog>
</template>
